import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { useTranslation } from 'react-i18next'

import Switch from 'components/Shared/Controls/Switch'
import CoCreatePanelHeader from './CoCreatePanelHeader'
import { groupMediaBySubGroup } from '../../buildMediaListItems'
import { buildMediaSlots } from '../../media'
import ErrorMessage from 'components/Shared/ErrorMessage'
import InfoBox from 'components/Shared/InfoBox'
import classnames from 'classnames'

const isSubGroupFullyFilled = (items) => {
  const slots = buildMediaSlots(items)
  return slots.length > 0 && slots.every((slot) => Boolean(slot.image))
}

export default function CoCreateImagePanel({
  variant,
  setVariant,
  selectedSubGroupId = null,
  setSelectedSubGroupId,
  media = [],
  headlines = [],
  maxItems = 10,
  generatedMediaCount = 0,
  showGenerationError = false,
  onGenerate,
  isCampaignRunning = false,
  onRequestHeadlines,
  onViewVariants,
}) {
  const filteredHeadlines = useMemo(
    () => headlines.map((headline) => headline?.trim()).filter(Boolean),
    [headlines],
  )
  const hasHeadlines = filteredHeadlines.length > 0
  const countToCreate = filteredHeadlines.length

  const mediaGroups = useMemo(() => groupMediaBySubGroup(media), [media])
  const mediaGroupsCount = mediaGroups.length
  const remainingSlots = Math.max(0, maxItems - mediaGroupsCount - (generatedMediaCount ?? 0))
  const canUseHeadlines = hasHeadlines && remainingSlots >= countToCreate
  const countToRemove = Math.max(0, countToCreate - remainingSlots)

  const allAspectRatioSlotsFilled = useMemo(() => {
    if (!selectedSubGroupId) return false

    const group = mediaGroups.find((g) => String(g.groupKey) === String(selectedSubGroupId))
    return group ? isSubGroupFullyFilled(group.items) : false
  }, [selectedSubGroupId, mediaGroups])

  const isGenerationLimitReached = useMemo(() => {
    if (remainingSlots !== 0 || !mediaGroups.length) return false

    return mediaGroups.every((group) => isSubGroupFullyFilled(group.items))
  }, [remainingSlots, mediaGroups])

  const canUseAspectRatio = !allAspectRatioSlotsFilled
  const isMidGenerationFlow = variant === 'ready' || variant === 'generating'
  const isPanelDisabled = isCampaignRunning || (isGenerationLimitReached && !isMidGenerationFlow)

  const [aspectRatio, setAspectRatio] = useState(canUseAspectRatio)
  const [headlineOverlay, setHeadlineOverlay] = useState(canUseHeadlines)

  const { t } = useTranslation()

  const prevVariantRef = useRef(variant)
  const manuallyDisabledAspectRatioRef = useRef(false)

  useEffect(() => {
    if (prevVariantRef.current === 'closed' && variant === 'open') {
      setHeadlineOverlay(canUseHeadlines)
      setAspectRatio(canUseAspectRatio)
      manuallyDisabledAspectRatioRef.current = false
    }
    prevVariantRef.current = variant
  }, [variant, canUseHeadlines, canUseAspectRatio])

  useEffect(() => {
    if (!canUseHeadlines) {
      setHeadlineOverlay(false)
    }
  }, [canUseHeadlines])

  useEffect(() => {
    if (!canUseAspectRatio) {
      setAspectRatio(false)
      return
    }

    if (!manuallyDisabledAspectRatioRef.current) {
      setAspectRatio(true)
    }
  }, [selectedSubGroupId, canUseAspectRatio])

  useEffect(() => {
    if (!isGenerationLimitReached || variant !== 'open') {
      return
    }

    setVariant('closed')
    setSelectedSubGroupId?.(null)
  }, [isGenerationLimitReached, variant, setVariant, setSelectedSubGroupId])

  const isGenerateButtonEnabled = (aspectRatio || headlineOverlay) && Boolean(selectedSubGroupId)

  const handleGenerate = () => {
    onGenerate?.({
      media,
      selectedSubGroupId,
      ratioVariants: aspectRatio ? 'true' : null,
      headlines: headlineOverlay ? filteredHeadlines : [],
    })
  }

  const headlineTooltipBody = !hasHeadlines
    ? t('Tips.EventEdit.coCreateHeadlineTooltip.noHeadlines')
    : t('Tips.EventEdit.coCreateHeadlineTooltip.limitReached', {
      countToRemove,
      countToCreate,
    })

  const showHeadlineTooltip = !canUseHeadlines
  const showAspectRatioTooltip = allAspectRatioSlotsFilled
  const panelTooltipBody = isCampaignRunning
    ? t('Tips.EventEdit.coCreatePanelTooltip.body')
    : t('Tips.EventEdit.coCreatePanelLimitReached.body')

  // return
  return (
    <>
      {showGenerationError && (
        <ErrorMessage variant="light" style={{ justifyContent: 'flex-start' }}>
          {t('Recommendations.campaign.setup.coCreatePanel.generatingError')}
        </ErrorMessage>
      )}

      <InfoBox
        enabled={isPanelDisabled}
        body={panelTooltipBody}
        withAnchor={false}
      >
        <div
          className={classnames('co-create-panel', {
            'is-clickable': variant === 'closed' && !isPanelDisabled,
            'is-disabled': isPanelDisabled,
          })}
          onClick={() => {
            if (isPanelDisabled || variant !== 'closed') {
              return
            }

            setVariant('open')
          }}
        >
          <CoCreatePanelHeader
            variant={variant}
            isGenerateButtonDisabled={!isGenerateButtonEnabled || isPanelDisabled}
            isPanelDisabled={isPanelDisabled}
            setVariant={setVariant}
            setSelectedSubGroupId={setSelectedSubGroupId}
            onGenerate={handleGenerate}
            onViewVariants={onViewVariants}
          />

          {variant === 'open' && (
            <div className="co-create-panel-content">
              <InfoBox
                enabled={showAspectRatioTooltip}
                body={t('Tips.EventEdit.coCreateAspectRatioTooltip.body')}
                withAnchor={false}
              >
                <div className="switch-wrapper">
                  <Switch
                    checked={aspectRatio}
                    disabled={!canUseAspectRatio}
                    onClick={() => {
                      if (!canUseAspectRatio) return

                      setAspectRatio((prev) => {
                        const next = !prev
                        manuallyDisabledAspectRatioRef.current = !next
                        return next
                      })
                    }}
                  />

                  <div>
                    <p className="title">
                      {t('Recommendations.campaign.setup.coCreatePanel.generateAspectRatio.title')}
                    </p>
                    <p className="description">
                      {t(
                        'Recommendations.campaign.setup.coCreatePanel.generateAspectRatio.description',
                      )}
                    </p>
                  </div>
                </div>
              </InfoBox>

              <InfoBox enabled={showHeadlineTooltip} body={headlineTooltipBody} withAnchor={false}>
                <div className="switch-wrapper" onClick={() => {
                  if (!hasHeadlines) {
                    onRequestHeadlines?.()
                    return
                  }
                }}>
                  <Switch
                    checked={headlineOverlay}
                    disabled={!canUseHeadlines}
                    onClick={() => canUseHeadlines && setHeadlineOverlay((prev) => !prev)}
                  />

                  <div>
                    <p className="title">
                      {t('Recommendations.campaign.setup.coCreatePanel.headlineOverlay.title')}
                    </p>
                    <p className="description">
                      {t(
                        'Recommendations.campaign.setup.coCreatePanel.headlineOverlay.description',
                      )}
                    </p>
                  </div>
                </div>
              </InfoBox>
            </div>
          )}
        </div>
      </InfoBox>
    </>
  )
}

CoCreateImagePanel.propTypes = {
  variant: PropTypes.oneOf(['closed', 'open', 'generating', 'ready']).isRequired,
  setVariant: PropTypes.func.isRequired,
  selectedSubGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedSubGroupId: PropTypes.func,
  media: PropTypes.arrayOf(PropTypes.object),
  headlines: PropTypes.arrayOf(PropTypes.string),
  maxItems: PropTypes.number,
  generatedMediaCount: PropTypes.number,
  showGenerationError: PropTypes.bool,
  onGenerate: PropTypes.func,
  isCampaignRunning: PropTypes.bool,
  onRequestHeadlines: PropTypes.func,
  onViewVariants: PropTypes.func,
}
